import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 📦 Wiki - Seção Produtos que Promovo
 * 
 * Documentação completa sobre como gerenciar produtos que você promove como afiliado.
 * Explica como encontrar produtos, solicitar afiliação e acompanhar comissões.
 * Voltada para afiliados (não técnica).
 */
const ProdutosQuePromovoWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:share-variant" />
          <span>Produtos que Promovo</span>
        </div>
        <p className={styles.heroText}>
          Gerencie todos os produtos que você promove como afiliado. Veja suas comissões, 
          acesse links de afiliação e acompanhe o desempenho de cada produto.
        </p>
      </section>

      {/* O que são Produtos que Promovo */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que são "Produtos que Promovo"?</span>
          <p className={styles.sectionSubtitle}>
            Seus produtos aprovados para promoção como afiliado
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              <strong>"Produtos que Promovo"</strong> são todos os produtos de outros produtores 
              que você foi <strong>aprovado para promover</strong> como afiliado. Quando você 
              solicita se tornar afiliado de um produto e é aprovado, ele aparece nesta lista.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Para cada produto, você pode ver suas <strong>comissões</strong>, acessar seu 
              <strong>link de afiliação único</strong>, ver informações do produtor e acompanhar 
              o desempenho das vendas.
            </p>
          </div>
        </div>
      </section>

      {/* Como Acessar */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Acessar?</span>
          <p className={styles.sectionSubtitle}>
            Encontre no menu lateral
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:menu" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Menu Lateral
                </span>
                <p>
                  No <strong>menu lateral</strong>, acesse <strong>Produtos → Produtos que Promovo</strong>.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como Funciona */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Funciona?</span>
          <p className={styles.sectionSubtitle}>
            Do pedido de afiliação até a promoção
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:store-search" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Encontre um Produto na Loja
                </span>
                <p>
                  Acesse a <strong>Loja</strong> e navegue pelos produtos disponíveis. 
                  Quando encontrar um produto interessante, clique nele para ver os detalhes.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:hand-coin" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Clique em "Promover este Produto"
                </span>
                <p>
                  Na página do produto, clique no botão <strong>"Promover este Produto"</strong>. 
                  Isso enviará uma solicitação de afiliação para o produtor.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:clock-outline" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Aguarde Aprovação
                </span>
                <p>
                  O produtor receberá uma notificação e irá <strong>analisar sua solicitação</strong>. 
                  Você receberá uma notificação quando for aprovado ou rejeitado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Produto Aparece em "Produtos que Promovo"
                </span>
                <p>
                  Após a aprovação, o produto aparecerá na lista de <strong>"Produtos que Promovo"</strong>. 
                  Você poderá acessar seu link de afiliação e começar a promover!
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Informações dos Cards */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Informações dos Cards</span>
          <p className={styles.sectionSubtitle}>
            O que cada informação significa
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Status</div>
                <span className={styles.cardTitle}>Status da Afiliação</span>
                <p>
                  Mostra se você está <strong>Aprovado</strong> (verde), <strong>Pendente</strong> 
                  (amarelo) ou <strong>Inativo</strong> (cinza). Apenas produtos aprovados 
                  geram comissões.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:percent" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Comissão</div>
                <span className={styles.cardTitle}>Taxa de Comissão</span>
                <p>
                  É o <strong>percentual de comissão</strong> que você recebe por cada venda 
                  realizada através do seu link. Definido pelo produtor ao aprovar sua solicitação.
                </p>
                <div className={styles.exampleBox}>
                  <Icon icon="mdi:calculator" />
                  <span><strong>Exemplo:</strong> 25% = R$ 25,00 de cada R$ 100,00 vendido</span>
                </div>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:account" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Produtor</div>
                <span className={styles.cardTitle}>Nome do Produtor</span>
                <p>
                  Mostra o <strong>nome do produtor</strong> que criou o produto. Você pode 
                  entrar em contato com ele através do email fornecido.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:calendar" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Data</div>
                <span className={styles.cardTitle}>Afiliado Desde</span>
                <p>
                  Mostra a <strong>data em que você foi aprovado</strong> como afiliado deste 
                  produto. Útil para acompanhar há quanto tempo você promove o produto.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Ver Detalhes do Produto */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Ver Detalhes do Produto</span>
          <p className={styles.sectionSubtitle}>
            Acesse informações completas e link de afiliação
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:eye" className={styles.infoIcon} />
          <div>
            <p>
              Ao clicar no botão <strong>"Ver Produto"</strong> em qualquer card, você será 
              direcionado para uma página especial com:
            </p>
            <ul style={{ marginTop: '12px', marginBottom: 0, paddingLeft: '20px' }}>
              <li><strong>Informações completas do produto:</strong> Descrição, preços, planos disponíveis</li>
              <li><strong>Link de afiliação único:</strong> Seu link personalizado para compartilhar</li>
              <li><strong>Estatísticas de vendas:</strong> Quantas vendas você gerou e quanto ganhou</li>
              <li><strong>Comissões acumuladas:</strong> Valor total de comissões pendentes e pagas</li>
              <li><strong>Dados do produtor:</strong> Contato e informações para comunicação</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Link de Afiliação */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Usar o Link de Afiliação?</span>
          <p className={styles.sectionSubtitle}>
            Compartilhe e ganhe comissões
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:link-variant" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Copie o Link
                </span>
                <p>
                  Na página de detalhes do produto, você encontrará seu <strong>link de afiliação único</strong>. 
                  Clique no botão <strong>"Copiar Link"</strong> para copiá-lo.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:share-variant" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Compartilhe o Link
                </span>
                <p>
                  Compartilhe o link em suas <strong>redes sociais</strong>, <strong>WhatsApp</strong>, 
                  <strong>email</strong>, ou qualquer outro canal. Qualquer pessoa que comprar através 
                  do seu link gera comissão para você!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:cookie" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Cookie é Salvo
                </span>
                <p>
                  Quando alguém clica no seu link, um <strong>cookie é salvo no navegador</strong> 
                  da pessoa. Se ela comprar dentro do período de validade do cookie, você recebe a comissão!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:cash-multiple" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Receba a Comissão
                </span>
                <p>
                  Quando a venda for confirmada, a <strong>comissão é calculada automaticamente</strong> 
                  e adicionada ao seu saldo. Você pode acompanhar tudo na página de comissões!
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Dicas e Boas Práticas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas e Boas Práticas</span>
          <p className={styles.sectionSubtitle}>
            Como ser um afiliado de sucesso
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:target" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Escolha Produtos Relevantes
                </span>
                <p>
                  Promova apenas produtos que você <strong>conhece e acredita</strong>. Seu público 
                  confia em você, então seja seletivo!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:content-copy" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Use o Link Correto
                </span>
                <p>
                  Sempre use o <strong>link de afiliação único</strong> fornecido na plataforma. 
                  Links genéricos não geram comissões!
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:chart-line" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Acompanhe o Desempenho
                </span>
                <p>
                  Monitore regularmente suas <strong>estatísticas de vendas</strong> e comissões. 
                  Isso ajuda a identificar quais produtos têm melhor desempenho.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:account-group" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Mantenha Contato com o Produtor
                </span>
                <p>
                  Se tiver dúvidas sobre o produto ou precisar de materiais de divulgação, 
                  <strong>entre em contato com o produtor</strong>. A comunicação é importante!
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Tire suas dúvidas sobre produtos promovidos
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso promover quantos produtos quiser?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim! Não há limite de produtos que você pode promover. Quanto mais produtos 
                  relevantes você promover, mais oportunidades de ganhar comissões você terá.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Quanto tempo leva para receber a comissão?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  As comissões são calculadas automaticamente quando a venda é confirmada. 
                  O valor fica disponível para saque após o período de liberação (D+), 
                  que varia conforme a configuração do produto.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>O que acontece se minha solicitação for rejeitada?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Se sua solicitação for rejeitada, o produto não aparecerá na lista de 
                  "Produtos que Promovo". Você pode tentar solicitar novamente no futuro, 
                  mas é recomendado entrar em contato com o produtor para entender o motivo.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso promover produtos de qualquer categoria?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim! Você pode promover produtos de qualquer categoria disponível na loja. 
                  O importante é escolher produtos que façam sentido para seu público e que 
                  você realmente recomende.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default ProdutosQuePromovoWiki;

